/**
 * Scanner de présences pour le personnel
 * Scanner pour gérer les présences de tout le personnel (enseignants, comptables, surveillants, etc.)
 */

import React, { useState, useEffect, useRef } from 'react';
import { 
    Card, 
    Button, 
    Alert, 
    Container, 
    Row, 
    Col, 
    Table, 
    Badge, 
    Spinner, 
    ButtonGroup, 
    Form,
    Toast,
    ToastContainer
} from 'react-bootstrap';
import { 
    QrCodeScan, 
    CheckCircleFill, 
    Calendar, 
    Clock, 
    ArrowRightCircle, 
    ArrowLeftCircle,
    PersonBadge,
    Wifi,
    PersonCheck,
    PersonX
} from 'react-bootstrap-icons';
import { useAuth } from '../../hooks/useAuth';
import { secureApiEndpoints } from '../../utils/apiMigration';
import QrScanner from 'qr-scanner';

const TeacherAttendanceScanner = () => {
    const [isScanning, setIsScanning] = useState(false);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('');
    const [todayAttendances, setTodayAttendances] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [scannerError, setScannerError] = useState('');
    const [showManualInput, setShowManualInput] = useState(false);
    const [manualQrCode, setManualQrCode] = useState('');
    const [eventType, setEventType] = useState('auto');
    const [entryExitStats, setEntryExitStats] = useState(null);
    const [toastList, setToastList] = useState([]);

    const { user } = useAuth();
    
    // Valeurs par défaut sans mode offline
    const isOnline = true;
    const isSyncing = false;

    const videoRef = useRef(null);
    const qrScannerRef = useRef(null);

    // Le useEffect sera déplacé après les définitions des fonctions



    const loadTodayAttendances = async () => {
        try {
            setIsLoading(true);
            
            if (isOnline) {
                const response = await secureApiEndpoints.staff.getDailyAttendance({
                    supervisor_id: user.id
                });

                if (response.success) {
                    setTodayAttendances(response.data.attendances || []);
                }
            }
        } catch (error) {
            console.error('Erreur lors du chargement des présences:', error);
            showToast('Erreur lors du chargement des présences', 'danger');
        } finally {
            setIsLoading(false);
        }
    };

    const loadEntryExitStats = async () => {
        try {
            if (!isOnline) return;

            const response = await secureApiEndpoints.staff.getEntryExitStats({
                supervisor_id: user.id
            });

            if (response.success) {
                setEntryExitStats(response.data);
            }
        } catch (error) {
            console.error('Erreur lors du chargement des statistiques:', error);
        }
    };

    const loadStaffList = async () => {
        try {
            if (!isOnline) return;

            const response = await secureApiEndpoints.staff.getStaffWithQR();

            if (response.success) {
                // Liste du personnel chargée (mode offline supprimé)
            }
        } catch (error) {
            console.error('Erreur lors du chargement du personnel:', error);
        }
    };


    const handleScanResult = async (result) => {
        if (!result || result.trim() === '') return;

        console.log('🔍 QR Code personnel scanné:', result);
        setIsScanning(false);
        stopScanner();

        try {
            await processTeacherAttendanceScan(result);
        } catch (error) {
            console.error('Erreur lors du traitement du scan:', error);
            setMessage('Erreur lors du traitement du scan');
            setMessageType('danger');
        }
    };

    const processTeacherAttendanceScan = async (qrCode) => {
        try {
            setIsLoading(true);
            setMessage('');

            const response = await secureApiEndpoints.staff.scanQR({
                staff_qr_code: qrCode,
                supervisor_id: user.id,
                event_type: eventType
            });

            if (response.success) {
                const data = response.data;
                
                setMessage(`✅ ${response.message}`);
                setMessageType('success');
                await loadTodayAttendances();
                await loadEntryExitStats();
                
                // Afficher des informations détaillées
                if (data.punctuality_info) {
                    const info = data.punctuality_info;
                    let statusMessage = '';
                    
                    if (info.late_minutes > 0) {
                        statusMessage += ` (${info.late_minutes} min de retard)`;
                    }
                    if (info.early_departure_minutes > 0) {
                        statusMessage += ` (${info.early_departure_minutes} min de départ anticipé)`;
                    }
                    if (info.work_hours > 0) {
                        statusMessage += ` (${info.work_hours}h travaillées)`;
                    }
                    
                    if (statusMessage) {
                        showToast(`Présence enregistrée${statusMessage}`, 'info');
                    }
                }
                
                showToast('Présence personnel enregistrée', 'success');
            } else {
                setMessage(`❌ ${response.message}`);
                setMessageType('danger');
                showToast(response.message, 'danger');
            }

        } catch (error) {
            console.error('Erreur lors du traitement:', error);
            setMessage('❌ Erreur lors de l\'enregistrement de la présence');
            setMessageType('danger');
            showToast('Erreur lors de l\'enregistrement', 'danger');
        } finally {
            setIsLoading(false);
        }
    };

    const startScanner = async () => {
        try {
            setScannerError('');
            setIsScanning(true);
            
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            stream.getTracks().forEach(track => track.stop());
            
            const cameras = await QrScanner.listCameras(true);
            
            if (cameras.length === 0) {
                throw new Error('Aucune caméra trouvée sur cet appareil');
            }
            
            if (videoRef.current) {
                qrScannerRef.current = new QrScanner(
                    videoRef.current,
                    (result) => handleScanResult(result.data),
                    {
                        returnDetailedScanResult: true,
                        highlightScanRegion: true,
                        highlightCodeOutline: true,
                    }
                );
                
                await qrScannerRef.current.start();
                console.log('✅ Scanner QR personnel démarré avec succès');
            }
        } catch (error) {
            console.error('❌ Erreur scanner:', error);
            setScannerError(error.message);
            setIsScanning(false);
            showToast('Erreur du scanner: ' + error.message, 'danger');
        }
    };

    const stopScanner = () => {
        if (qrScannerRef.current) {
            qrScannerRef.current.stop();
            setIsScanning(false);
            console.log('⏹️ Scanner arrêté');
        }
    };

    const handleManualScan = async () => {
        if (manualQrCode.trim()) {
            await processTeacherAttendanceScan(manualQrCode.trim());
            setManualQrCode('');
            setShowManualInput(false);
        }
    };


    const showToast = (message, variant = 'info', icon = null) => {
        const id = Date.now();
        const newToast = {
            id,
            message,
            variant,
            icon,
            timestamp: new Date()
        };
        
        setToastList(prev => [...prev, newToast]);
        
        // Auto-suppression après 5 secondes
        setTimeout(() => {
            setToastList(prev => prev.filter(toast => toast.id !== id));
        }, 5000);
    };

    const getNetworkStatusBadge = () => {
        return <Badge bg="success" className="ms-2">
            <Wifi size={12} className="me-1" />
            Online
        </Badge>;
    };

    const getAttendanceTypeIcon = (eventType) => {
        return eventType === 'entry' ? (
            <ArrowRightCircle size={12} className="me-1" />
        ) : (
            <ArrowLeftCircle size={12} className="me-1" />
        );
    };

    const getAttendanceTypeBadge = (eventType) => {
        return eventType === 'entry' ? (
            <Badge bg="success" size="sm">
                {getAttendanceTypeIcon(eventType)}
                Entrée
            </Badge>
        ) : (
            <Badge bg="warning" size="sm">
                {getAttendanceTypeIcon(eventType)}
                Sortie
            </Badge>
        );
    };

    const getRoleLabel = (role) => {
        const roleLabels = {
            'teacher': 'Enseignant',
            'accountant': 'Comptable',
            'comptable_superieur': 'Comptable Supérieur',
            'general_accountant': 'Comptable Général',
            'surveillant_general': 'Surveillant Général',
            'admin': 'Administrateur',
            'secretaire': 'Secrétaire'
        };
        return roleLabels[role] || role;
    };

    // Effet pour charger les données au montage du composant
    useEffect(() => {
        loadTodayAttendances();
        loadEntryExitStats();
        loadStaffList();
        
        return () => {
            if (qrScannerRef.current) {
                qrScannerRef.current.destroy();
            }
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Container fluid className="py-4">
            {/* Header avec statut réseau */}
            <Row className="mb-4">
                <Col>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h2 className="mb-0 d-flex align-items-center">
                                <PersonBadge className="me-2" />
                                Scanner Présences Personnel
                                {getNetworkStatusBadge()}
                            </h2>
                            <p className="text-muted mb-0">
                                Surveillance: {user?.name}
                            </p>
                        </div>
                        
                    </div>
                </Col>
            </Row>

            {/* Statistiques rapides */}
            {entryExitStats && entryExitStats.stats_by_type && (
                <Row className="mb-4">
                    <Col md={3}>
                        <Card className="text-center">
                            <Card.Body>
                                <PersonCheck size={24} className="text-success mb-2" />
                                <h4>
                                    {Object.values(entryExitStats.stats_by_type).reduce((sum, stats) => sum + (stats.present_count || 0), 0)}
                                </h4>
                                <small className="text-muted">Présents</small>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="text-center">
                            <Card.Body>
                                <ArrowRightCircle size={24} className="text-primary mb-2" />
                                <h4>
                                    {Object.values(entryExitStats.stats_by_type).reduce((sum, stats) => sum + (stats.entries || 0), 0)}
                                </h4>
                                <small className="text-muted">Entrées</small>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="text-center">
                            <Card.Body>
                                <ArrowLeftCircle size={24} className="text-warning mb-2" />
                                <h4>
                                    {Object.values(entryExitStats.stats_by_type).reduce((sum, stats) => sum + (stats.exits || 0), 0)}
                                </h4>
                                <small className="text-muted">Sorties</small>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col md={3}>
                        <Card className="text-center">
                            <Card.Body>
                                <Clock size={24} className="text-danger mb-2" />
                                <h4>
                                    {Object.values(entryExitStats.stats_by_type).reduce((sum, stats) => sum + (stats.late_count || 0), 0)}
                                </h4>
                                <small className="text-muted">En retard</small>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}


            {/* Interface de scan principale */}
            <Row>
                <Col lg={6} className="mb-4">
                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center">
                                <QrCodeScan className="me-2" />
                                Scanner QR Code Personnel
                            </div>
                            <ButtonGroup size="sm">
                                <Button 
                                    variant="outline-secondary"
                                    onClick={() => setShowManualInput(!showManualInput)}
                                >
                                    Saisie manuelle
                                </Button>
                            </ButtonGroup>
                        </Card.Header>
                        
                        <Card.Body>
                            {/* Contrôles du scanner */}
                            <div className="mb-3">
                                <div className="d-flex gap-2 mb-3">
                                    <Button 
                                        variant={isScanning ? "danger" : "success"}
                                        onClick={isScanning ? stopScanner : startScanner}
                                        disabled={isLoading}
                                    >
                                        {isScanning ? "Arrêter" : "Démarrer"} Scanner
                                    </Button>
                                    
                                    <Form.Select 
                                        value={eventType} 
                                        onChange={(e) => setEventType(e.target.value)}
                                        style={{ width: 'auto' }}
                                    >
                                        <option value="auto">Auto-détection</option>
                                        <option value="entry">Entrée forcée</option>
                                        <option value="exit">Sortie forcée</option>
                                    </Form.Select>
                                </div>

                                {/* Saisie manuelle */}
                                {showManualInput && (
                                    <div className="mb-3">
                                        <div className="d-flex gap-2">
                                            <Form.Control
                                                type="text"
                                                placeholder="Code QR personnel"
                                                value={manualQrCode}
                                                onChange={(e) => setManualQrCode(e.target.value)}
                                                onKeyPress={(e) => e.key === 'Enter' && handleManualScan()}
                                            />
                                            <Button 
                                                variant="primary" 
                                                onClick={handleManualScan}
                                                disabled={!manualQrCode.trim() || isLoading}
                                            >
                                                Scanner
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Zone de scan vidéo */}
                            <div className="text-center">
                                <video 
                                    ref={videoRef}
                                    style={{ 
                                        width: '100%', 
                                        maxWidth: '400px',
                                        height: '300px',
                                        border: '2px solid #dee2e6',
                                        borderRadius: '8px',
                                        display: isScanning ? 'block' : 'none'
                                    }}
                                />
                                
                                {!isScanning && (
                                    <div 
                                        className="d-flex align-items-center justify-content-center text-muted"
                                        style={{ height: '300px', border: '2px dashed #dee2e6', borderRadius: '8px' }}
                                    >
                                        <div className="text-center">
                                            <PersonBadge size={48} className="mb-2" />
                                            <p>Scanner QR Code Personnel</p>
                                            <small className="text-muted">Cliquez sur "Démarrer Scanner"</small>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Messages et erreurs */}
                            {scannerError && (
                                <Alert variant="danger" className="mt-3">
                                    {scannerError}
                                </Alert>
                            )}
                            
                            {message && (
                                <Alert variant={messageType} className="mt-3">
                                    {message}
                                </Alert>
                            )}

                            {/* Loader */}
                            {isLoading && (
                                <div className="text-center mt-3">
                                    <Spinner animation="border" variant="primary" />
                                    <p className="mt-2 text-muted">Traitement en cours...</p>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                {/* Liste des présences du jour */}
                <Col lg={6} className="mb-4">
                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center">
                                <Calendar className="me-2" />
                                Présences Personnel du jour
                            </div>
                            <div>
                                {isSyncing && <Spinner animation="border" size="sm" className="me-2" />}
                                <Button 
                                    variant="outline-primary" 
                                    size="sm"
                                    onClick={loadTodayAttendances}
                                    disabled={isLoading}
                                >
                                    Actualiser
                                </Button>
                            </div>
                        </Card.Header>
                        
                        <Card.Body style={{ maxHeight: '500px', overflowY: 'auto' }}>
                            {todayAttendances.length > 0 ? (
                                <Table responsive size="sm">
                                    <thead>
                                        <tr>
                                            <th>Heure</th>
                                            <th>Personnel</th>
                                            <th>Type</th>
                                            <th>Statut</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {todayAttendances.map((attendance, index) => (
                                            <tr key={index}>
                                                <td>
                                                    <small>
                                                        <Clock size={12} className="me-1" />
                                                        {attendance.scanned_at || attendance.timestamp ? 
                                                            new Date(attendance.scanned_at || attendance.timestamp).toLocaleTimeString() : 
                                                            'N/A'
                                                        }
                                                    </small>
                                                </td>
                                                <td>
                                                    <small>
                                                        {attendance.user?.name || attendance.teacher?.full_name || attendance.teacherName || attendance.staffName || 'Inconnu'}
                                                        {attendance.user?.role && (
                                                            <span className="text-muted"> - {getRoleLabel(attendance.user.role)}</span>
                                                        )}
                                                    </small>
                                                </td>
                                                <td>
                                                    {getAttendanceTypeBadge(attendance.event_type || attendance.eventType)}
                                                </td>
                                                <td>
                                                    <Badge bg="success" size="sm">
                                                        <CheckCircleFill size={12} />
                                                    </Badge>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            ) : (
                                <div className="text-center text-muted py-4">
                                    <PersonX size={32} className="mb-2" />
                                    <p>Aucune présence personnel enregistrée aujourd'hui</p>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>


            {/* Toasts de notification */}
            <ToastContainer position="top-end" className="p-3">
                {toastList.map(toast => (
                    <Toast 
                        key={toast.id}
                        bg={toast.variant}
                        show={true}
                        onClose={() => setToastList(prev => prev.filter(t => t.id !== toast.id))}
                        delay={5000}
                        autohide
                    >
                        <Toast.Body className="text-white">
                            {toast.icon && <span className="me-2">{toast.icon}</span>}
                            {toast.message}
                        </Toast.Body>
                    </Toast>
                ))}
            </ToastContainer>
        </Container>
    );
};

export default TeacherAttendanceScanner;